import axios from 'axios'
import moment from 'moment'
import styled from 'styled-components'
import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState, } from 'react'
import { Close as CloseIcon, Image as ImageIcon, Send as SendIcon, } from '@mui/icons-material'
import { useSelector, } from 'react-redux'

import { API_BASE_URL, urls, } from '@globals'

import { RootState, } from '@reducers'

import { Agent, } from '@types'

type ChatRoom = {
  id: number
  agent: {
    id: number
    name: string
    mobile_number: string
    agent_profile_id: number
  }
  created_at: string
  last_message_at: string | null
  unread_count: number
}

type ChatMessage = {
  id: number
  room: number
  sender?: {
    id: number
    name: string
    mobile_number: string
  }
  sender_type: 'AGENT' | 'CONTROL_ROOM'
  text?: string | null
  image?: string | null
  created_at: string
  is_mine?: boolean
}

type WebSocketMessage =
  | {
      type: 'message'
      message: ChatMessage
    }
  | {
      type: 'error'
      code: string
    }

interface Props {
  agent: Agent
  onClose: () => void
  onMessagesRead?: () => void
  className?: string
}

const Container = styled.div`
  border-top: 1px solid rgba(203, 213, 225, 0.85);
  background-color: rgba(248, 250, 252, 0.94);

  .chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.95);
  }

  .chat-title {
    min-width: 0;
  }

  .chat-title strong {
    display: block;
    color: rgb(30, 41, 59);
    font-size: 14px;
  }

  .chat-title span {
    display: block;
    margin-top: 3px;
    color: rgb(100, 116, 139);
    font-size: 12px;
  }

  .close-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background-color: transparent;
    color: rgb(71, 85, 105);
    cursor: pointer;
  }

  .message-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 280px;
    padding: 12px;
    overflow-y: auto;
  }

  .message-row {
    display: flex;

    &.mine {
      justify-content: flex-end;
    }
  }

  .message-bubble {
    max-width: 82%;
    padding: 9px 10px;
    border-radius: 12px;
    background-color: white;
    border: 1px solid rgba(226, 232, 240, 0.9);
    color: rgb(51, 65, 85);
    font-size: 13px;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .message-row.mine .message-bubble {
    background-color: rgb(67, 81, 249);
    border-color: rgb(67, 81, 249);
    color: white;
  }

  .message-meta {
    display: block;
    margin-top: 5px;
    color: rgb(148, 163, 184);
    font-size: 11px;
  }

  .message-row.mine .message-meta {
    color: rgba(255, 255, 255, 0.72);
  }

  .message-image {
    display: block;
    max-width: 100%;
    max-height: 180px;
    margin-bottom: 7px;
    border-radius: 8px;
    object-fit: cover;
  }

  .empty-message, .status-message {
    margin: auto;
    color: rgb(100, 116, 139);
    font-size: 13px;
    text-align: center;
  }

  .error-message {
    padding: 9px 12px 0;
    color: rgb(220, 38, 38);
    font-size: 12px;
  }

  .message-form {
    display: flex;
    gap: 8px;
    padding: 10px 12px 12px;
    border-top: 1px solid rgba(226, 232, 240, 0.95);
  }

  .attachment-preview {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px 0;
    color: rgb(71, 85, 105);
    font-size: 12px;
  }

  .attachment-preview span {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attachment-preview button {
    border: 0;
    background: transparent;
    color: rgb(100, 116, 139);
    cursor: pointer;
  }

  .message-input {
    flex: 1;
    min-width: 0;
    height: 38px;
    padding: 0 11px;
    border: 1px solid rgba(203, 213, 225, 0.95);
    border-radius: 10px;
    background-color: white;
    color: rgb(30, 41, 59);
    font-size: 13px;
    outline: none;
  }

  .message-input:focus {
    border-color: rgba(67, 81, 249, 0.7);
  }

  .image-button, .send-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background-color: rgb(67, 81, 249);
    color: white;
    cursor: pointer;
  }

  .image-button {
    background-color: rgb(71, 85, 105);
  }

  .image-button:disabled, .send-button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .file-input {
    display: none;
  }
`

const sortMessages = (messages: Array<ChatMessage>) => {
  return [ ... messages, ].sort((a, b) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  })
}

const normalizeMessages = (data: unknown): Array<ChatMessage> => {
  if(Array.isArray(data)) {
    return sortMessages(data)
  }

  if(typeof data === 'object' && data !== null && 'results' in data) {
    const { results, } = data as { results?: unknown }

    if(Array.isArray(results)) {
      return sortMessages(results)
    }
  }

  return []
}

const getChatWebSocketUrl = (roomId: number, token: string) => {
  const url = new URL(API_BASE_URL)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = `/ws/chats/agent-control-room/${ roomId }/`
  url.search = ''
  url.searchParams.set('token', token)

  return url.toString()
}

function AgentChatPanel(props: Props) {
  const { agent, onClose, onMessagesRead, className, } = props

  const token = useSelector((state: RootState) => state.account.token)

  const wsRef = useRef<WebSocket | null>(null)
  const messageListRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [ room, setRoom, ] = useState<ChatRoom | null>(null)
  const [ messages, setMessages, ] = useState<Array<ChatMessage>>([])
  const [ draft, setDraft, ] = useState('')
  const [ selectedImage, setSelectedImage, ] = useState<File | null>(null)
  const [ errorMessage, setErrorMessage, ] = useState('')
  const [ isLoading, setIsLoading, ] = useState(false)
  const [ isSending, setIsSending, ] = useState(false)
  const [ connectionState, setConnectionState, ] = useState<'idle' | 'connecting' | 'open' | 'closed'>('idle')

  const authHeaders = useMemo(
    (): Record<string, string> | undefined => token ? { Authorization: `Bearer ${ token }`, } : undefined,
    [ token, ]
  )

  const appendMessage = useCallback(
    (message: ChatMessage) => {
      setMessages(previousMessages => {
        if(previousMessages.some(previousMessage => previousMessage.id === message.id)) {
          return previousMessages
        }

        return sortMessages(previousMessages.concat(message))
      })
    },
    []
  )

  const reloadMessages = useCallback(
    async (roomId: number) => {
      const response = await axios.get(
        `${ urls.users.CONTROL_ROOM_CHAT_ROOMS }/${ roomId }/messages`,
        { headers: authHeaders, }
      )

      setMessages(normalizeMessages(response.data))
    },
    [ authHeaders, ]
  )

  useEffect(
    () => {
      if(!token) {
        setErrorMessage('로그인 정보가 없어 채팅을 연결할 수 없습니다.')
        return
      }

      let isCancelled = false

      const loadRoom = async () => {
        setIsLoading(true)
        setErrorMessage('')
        setRoom(null)
        setMessages([])

        try {
          const roomResponse = await axios.post(
            `${ urls.users.CONTROL_ROOM_CHAT_ROOMS }/agents/${ agent.id }`,
            null,
            { headers: authHeaders, }
          )

          if(isCancelled) {
            return
          }

          const nextRoom = roomResponse.data
          setRoom(nextRoom)
          await reloadMessages(nextRoom.id)
          onMessagesRead?.()
        } catch (error) {
          if(!isCancelled) {
            setErrorMessage('채팅방을 불러오지 못했습니다.')
          }
        } finally {
          if(!isCancelled) {
            setIsLoading(false)
          }
        }
      }

      loadRoom()

      return () => {
        isCancelled = true
      }
    },
    [ agent.id, authHeaders, onMessagesRead, reloadMessages, token, ]
  )

  useEffect(
    () => {
      if(!room || !token) {
        return
      }

      let reconnectTimer: ReturnType<typeof setTimeout> | null = null
      let shouldReconnect = true

      const connect = () => {
        setConnectionState('connecting')

        const socket = new WebSocket(getChatWebSocketUrl(room.id, token))
        wsRef.current = socket

        socket.onopen = () => {
          setConnectionState('open')
          setErrorMessage('')
        }

        socket.onmessage = (event) => {
          const data: WebSocketMessage = JSON.parse(event.data)

          if(data.type === 'message') {
            appendMessage(data.message)
          } else if(data.type === 'error') {
            setErrorMessage(`채팅 전송 오류: ${ data.code }`)
          }
        }

        socket.onerror = () => {
          setErrorMessage('채팅 연결에 문제가 발생했습니다.')
        }

        socket.onclose = (event) => {
          if(wsRef.current === socket) {
            wsRef.current = null
          }

          setConnectionState('closed')

          if(event.code === 4401) {
            setErrorMessage('채팅 인증에 실패했습니다.')
            return
          }

          if(event.code === 4403) {
            setErrorMessage('이 채팅방에 접근할 권한이 없습니다.')
            return
          }

          if(shouldReconnect) {
            reconnectTimer = setTimeout(connect, 2000)
          }
        }
      }

      connect()

      return () => {
        shouldReconnect = false

        if(reconnectTimer) {
          clearTimeout(reconnectTimer)
        }

        wsRef.current?.close()
        wsRef.current = null
      }
    },
    [ appendMessage, room, token, ]
  )

  useEffect(
    () => {
      if(messageListRef.current) {
        messageListRef.current.scrollTop = messageListRef.current.scrollHeight
      }
    },
    [ messages, ]
  )

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const text = draft.trim()

    if((!text && !selectedImage) || !room) {
      return
    }

    const socket = wsRef.current

    if(!selectedImage && socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ text, }))
      setDraft('')
      return
    }

    setIsSending(true)
    setErrorMessage('')

    try {
      const data = selectedImage ? new FormData() : { text, }

      if(selectedImage && data instanceof FormData) {
        if(text) {
          data.append('text', text)
        }

        data.append('image', selectedImage)
      }

      const response = await axios.post(
        `${ urls.users.CONTROL_ROOM_CHAT_ROOMS }/${ room.id }/messages`,
        data,
        { headers: authHeaders, }
      )

      setDraft('')
      setSelectedImage(null)

      if(response.data?.id) {
        appendMessage(response.data)
      } else {
        await reloadMessages(room.id)
      }
    } catch (error) {
      setErrorMessage('메시지를 전송하지 못했습니다.')
    } finally {
      setIsSending(false)
    }
  }

  const onImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if(file) {
      setSelectedImage(file)
    }

    event.target.value = ''
  }

  const connectionLabel = {
    idle: '대기 중',
    connecting: '연결 중',
    open: '실시간 연결됨',
    closed: '재연결 대기 중',
  }[connectionState]

  return (
    <Container className={ className }>
      <div className='chat-header'>
        <div className='chat-title'>
          <strong>{ agent.name } 평가사 채팅</strong>
          <span>{ connectionLabel }</span>
        </div>
        <button className='close-button' type='button' onClick={ onClose }>
          <CloseIcon fontSize='small' />
        </button>
      </div>
      {
        errorMessage && (
          <p className='error-message'>{ errorMessage }</p>
        )
      }
      <div className='message-list' ref={ messageListRef }>
        {
          isLoading ? (
            <p className='status-message'>채팅을 불러오는 중입니다.</p>
          ) : messages.length === 0 ? (
            <p className='empty-message'>아직 주고받은 메시지가 없습니다.</p>
          ) : (
            messages.map(message => {
              const isMine = message.is_mine === true || message.sender_type === 'CONTROL_ROOM'

              return (
                <div className={ `message-row ${ isMine ? 'mine' : '' }` } key={ message.id }>
                  <div className='message-bubble'>
                    {
                      message.image && (
                        <img
                          className='message-image'
                          src={ message.image }
                          alt='채팅 첨부 이미지'
                        />
                      )
                    }
                    { message.text }
                    <span className='message-meta'>
                      { moment(message.created_at).format('HH:mm') }
                    </span>
                  </div>
                </div>
              )
            })
          )
        }
      </div>
      {
        selectedImage && (
          <div className='attachment-preview'>
            <ImageIcon fontSize='small' />
            <span>{ selectedImage.name }</span>
            <button type='button' onClick={ () => setSelectedImage(null) }>
              제거
            </button>
          </div>
        )
      }
      <form className='message-form' onSubmit={ onSubmit }>
        <input
          ref={ fileInputRef }
          className='file-input'
          type='file'
          accept='image/*'
          onChange={ onImageSelect }
        />
        <button
          className='image-button'
          type='button'
          disabled={ isLoading || isSending || !room }
          onClick={ () => fileInputRef.current?.click() }>
          <ImageIcon fontSize='small' />
        </button>
        <input
          className='message-input'
          value={ draft }
          placeholder='메시지 입력'
          onChange={ event => setDraft(event.target.value) }
          disabled={ isLoading || !room }
        />
        <button
          className='send-button'
          type='submit'
          disabled={ isLoading || isSending || !room || (!draft.trim() && !selectedImage) }>
          <SendIcon fontSize='small' />
        </button>
      </form>
    </Container>
  )
}

export default AgentChatPanel
